import * as React from 'react'
import { Fragment } from 'react'
import { createTheme, styled, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import MuiDrawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import MuiAppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { DrawerMenu } from './DrawerMenu'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import { UserMenu } from './UserMenu'
import { Admin } from './Admin'
import { Clinic } from './Clinic'
import { Lab } from './Lab'
import { Training } from './Training'
import AdapterMoment from '@mui/lab/AdapterMoment'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { useUserDetails } from './firebase'
import { Loading } from './Loading'
import { LftCertMaker } from './LftCertMaker'

const drawerWidth = 240

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
)

const mdTheme = createTheme({ palette: { mode: 'light' } })

function DashboardContent() {
  const [user] = useUserDetails()
  const { dashboard } = user || {}
  const [open, setOpen] = React.useState(window.innerWidth > 600)
  const toggleDrawer = () => {
    setOpen(!open)
  }

  function getElementForRoute(r) {
    switch (r) {
      case '/':
        return <Home/>
      case '/clinic/lft-cert-maker':
        return <LftCertMaker/>
      default:
        return <Home/>
    }
  }

  return (
    <Router>

      <ThemeProvider theme={mdTheme}>
        <Box sx={{ display: 'flex' }}>
          <CssBaseline/>
          <AppBar position="absolute" open={open}>
            <Toolbar sx={{ pr: '24px' }}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={toggleDrawer}
                sx={{
                  marginRight: '36px',
                  ...(open && { display: 'none' }),
                }}>
                <MenuIcon/>
              </IconButton>
              <Typography
                component="h1"
                variant="h6"
                color="inherit"
                noWrap
                sx={{ flexGrow: 1 }}
              >Mitmark Health Suite</Typography>
              <UserMenu/>
            </Toolbar>
          </AppBar>
          <Drawer variant="permanent" open={open}>
            <Toolbar
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: [1],
              }}>
              <IconButton onClick={toggleDrawer}>
                <ChevronLeftIcon/>
              </IconButton>
            </Toolbar>
            <Divider/>
            <List style={{ height: '100%' }}><DrawerMenu/></List>
          </Drawer>
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[900],
              flexGrow: 1,
              height: '100vh',
              overflow: 'auto',
            }}
          >
            <Toolbar/>

            <Routes>
              <Route path="/clinic/*" element={<Clinic/>}/>
              <Route path="/admin/*" element={<Admin/>}/>
              <Route path="/lab/*" element={<Lab/>}/>
              <Route path="/training/*" element={<Training/>}/>
              <Route path="/" element={getElementForRoute(dashboard)}/>
            </Routes>
          </Box>
        </Box>
      </ThemeProvider>
    </Router>
  )
}

function Home() {
  return <Fragment/>
}

export function Main() {
  const [user] = useUserDetails()
  return <LocalizationProvider dateAdapter={AdapterMoment}>
    {user && <DashboardContent/> || <Loading/>}
  </LocalizationProvider>

}
